import type { ReactNode } from "react";

import { Button, EmptyState as HeroEmptyState, cn } from "@heroui/react";

interface EmptyStateProps {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

/**
 * Shown when a surface has no data *and that is fine* — no jobs yet, no API
 * keys yet. Always offers the next step rather than dead-ending.
 */
export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
}: EmptyStateProps) {
  return (
    <HeroEmptyState
      className={cn(
        "flex flex-col items-center gap-3 px-6 py-14 text-center",
        className,
      )}
    >
      {icon ? (
        <div className="bg-surface-secondary text-muted flex size-11 items-center justify-center rounded-full">
          {icon}
        </div>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <p className="text-base font-medium">{title}</p>
        {description ? (
          <p className="text-muted mx-auto max-w-sm text-sm leading-relaxed">
            {description}
          </p>
        ) : null}
      </div>
      {action ? <div className="mt-1">{action}</div> : null}
    </HeroEmptyState>
  );
}

interface ErrorStateProps {
  title?: string;
  description?: ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
}

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="size-5" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5.5" strokeLinecap="round" />
    <circle cx="12" cy="16.3" r="0.9" fill="currentColor" stroke="none" />
  </svg>
);

/**
 * Shown when something actually failed. Distinct from `EmptyState` on purpose:
 * "no data" and "we could not load your data" must never look alike, or users
 * conclude their jobs were lost.
 */
export function ErrorState({
  title = "Something went wrong",
  description = "We couldn't load this. Try again in a moment.",
  onRetry,
  retryLabel = "Try again",
  className,
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center gap-3 px-6 py-14 text-center",
        className,
      )}
    >
      <div className="bg-danger-soft text-danger flex size-11 items-center justify-center rounded-full">
        <AlertIcon />
      </div>
      <div className="flex flex-col gap-1.5">
        <p className="text-base font-medium">{title}</p>
        <p className="text-muted mx-auto max-w-sm text-sm leading-relaxed">
          {description}
        </p>
      </div>
      {onRetry ? (
        <Button variant="secondary" size="sm" onPress={onRetry} className="mt-1">
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}
